import * as React from "react";
import IconLarge from "../../../hiberUiKit/src/atoms/Icon/IconLarge";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import "../../v5/table/workspace/workspaceContainer.scss";
import User from "../../../model/user/User";
import {connect, Dispatch} from "react-redux";
import {RootState} from "../../../store/reducer/root/RootState";
import OrganisationUsersTable from "./OrganisationUsersTable";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import {PartnerBrandSearch} from "../../../model/partnerBrand/PartnerBrand";
import ButtonGroupWrapper from "../../../hiberUiKit/src/atoms/Buttons/ButtonGroupWrapper";
import {RouteComponentProps, withRouter} from "react-router";
import Routes from "../../../navigation/route/Route";
import {fetchPartnerBrands} from "../../../store/action/partnerBrand/partnerBrand";
import {OnFailure} from "../../../store/action/actionHelpers";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import {api} from "../../../api/api";
import {UserRole} from "../../../model/user/UserRole";
import ListFilter from "../../lead/table/ListFilter";
import Switch from "../../../hiberUiKit/src/atoms/Switch/Switch";
import Select from "react-select";
import PageSelectionComponent from "../../v5/table/paginator/PageSelectionComponent";

const PAGE_SIZE = 20;

const ROLE_OPTIONS = [
	{value: UserRole.Admin, label: 'Admin'},
	{value: UserRole.Agent, label: 'Agent'},
	{value: UserRole.SalesEngineer, label: 'Sales Engineer'},
	{value: UserRole.ThirdPartyEngineer, label: 'Third Party Engineer'},
	{value: UserRole.BusinessDevelopmentPartner, label: 'Business Development Partner'},
	{value: UserRole.PartnerTechnician, label: 'Partner Technician'},
];

interface UsersListState {
	query: string;
	role: string | null;
	includeDisabled: boolean;
	page: number;
	sort: { field: string; direction: 'asc' | 'desc' };
	data: { items: User[]; totalElements: number } | null;
	loading: boolean;
}

const DEFAULT_STATE: UsersListState = {
	query: '',
	role: null,
	includeDisabled: false,
	page: 0,
	sort: {field: 'firstName', direction: 'asc'},
	data: null,
	loading: false,
};

interface ReduxProps {
	partnerBrands: { [key: string]: PartnerBrandSearch };
}

interface DispatchProps {
	fetchPartnerBrands: (ids: string[], onFailure: OnFailure) => void;
}

type Props = ReduxProps & RouteComponentProps<{}> & DispatchProps;

class OrganisationUserList extends React.Component<Props, UsersListState> {

	private queryDebounceTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(props: Props) {
		super(props);
		const persisted = OrganisationUserList.loadPersistedState();
		this.state = {...DEFAULT_STATE, ...persisted};
	}

	componentDidMount(): void {
		this.fetchPage();
	}

	componentWillUnmount(): void {
		if (this.queryDebounceTimer) clearTimeout(this.queryDebounceTimer);
	}

	private static loadPersistedState(): Partial<UsersListState> {
		try {
			const raw = localStorage.getItem('usersListState');
			return raw ? JSON.parse(raw) : {};
		} catch {
			return {};
		}
	}

	private persistState = () => {
		const {query, role, includeDisabled, sort} = this.state;
		localStorage.setItem('usersListState', JSON.stringify({query, role, includeDisabled, sort}));
	};

	private fetchPage = () => {
		const {query, role, includeDisabled, page, sort} = this.state;
		this.setState({loading: true});
		api.instance.userApi.getUsersPage({
			query: query || undefined,
			role: role || undefined,
			includeDisabled,
			page,
			size: PAGE_SIZE,
			sort: `${sort.field},${sort.direction}`,
		})
			.then(data => {
				this.setState({data, loading: false});
				const pbids = data.items
					.filter(u => u.data.partnerBrandId.isDefined && !this.props.partnerBrands[u.data.partnerBrandId.orNull])
					.map(u => u.data.partnerBrandId.get);
				if (pbids.length > 0) {
					this.props.fetchPartnerBrands(pbids, (e) => notifyBugsnagAndShowToast(e, "Fetch partner brands"));
				}
			})
			.catch(() => this.setState({loading: false}));
	};

	private handleQueryChange = (value: string) => {
		this.setState({query: value});
		if (this.queryDebounceTimer) clearTimeout(this.queryDebounceTimer);
		this.queryDebounceTimer = setTimeout(() => {
			this.setState({page: 0}, () => {
				this.persistState();
				this.fetchPage();
			});
		}, 300);
	};

	private handleRoleChange = (role: string | null) => {
		this.setState({role, page: 0}, () => {
			this.persistState();
			this.fetchPage();
		});
	};

	private handleIncludeDisabledChange = (value: boolean) => {
		this.setState({includeDisabled: value, page: 0}, () => {
			this.persistState();
			this.fetchPage();
		});
	};

	private handleSortChange = (field: string) => {
		const direction =
			this.state.sort.field === field && this.state.sort.direction === 'asc'
				? 'desc'
				: 'asc';
		this.setState({sort: {field, direction}, page: 0}, () => {
			this.persistState();
			this.fetchPage();
		});
	};

	private handlePageChange = (page: number) => {
		this.setState({page}, () => this.fetchPage());
	};

	private handleClearFilters = () => {
		this.setState({...DEFAULT_STATE, data: this.state.data, loading: false}, () => {
			this.persistState();
			this.fetchPage();
		});
	};

	private renderPagination() {
		const {page, data} = this.state;
		if (!data) return null;

		return (
			<PageSelectionComponent
				itemsNumber={data.totalElements}
				itemsPerPage={PAGE_SIZE}
				currentPageIndex={page + 1}
				onChange={(index) => this.handlePageChange(index - 1)}
			/>
		);
	}

	render() {
		const {query, role, includeDisabled, data, loading} = this.state;
		const users = data ? data.items : [];

		return (
			<div className="c-workspace">
				<div className="c-workspace-title">
					<h1>
						<IconLarge icon={Icon.Users}/>
						<span>Organisation Users</span>
					</h1>
					<div>
						<ButtonGroupWrapper>
							<ButtonPrimary onClick={() => this.props.history.push(Routes.OrganisationUserCreate)}
										   label={"Create User"}
										   variant={"burgundy"}/>
						</ButtonGroupWrapper>
					</div>
				</div>
				<div className="c-workspace_actions">
					<div className="actions-wrapper c-users-filters" style={{alignItems: 'center'}}>
						<ListFilter
							filterValue={query}
							onFilterChange={this.handleQueryChange}
							isLoading={false}
							placeholder="Search by name or email..."
						/>
						<div className={"c-users-role-filter"} style={{width: '220px', marginLeft: '12px'}}>
							<Select
								className="c-select-component"
								classNamePrefix="c-select-component"
								options={ROLE_OPTIONS}
								value={ROLE_OPTIONS.find(o => o.value === role) || null}
								onChange={(opt: any) => this.handleRoleChange(opt ? opt.value : null)}
								placeholder="All roles"
								isSearchable={false}
								isClearable={true}
							/>
						</div>
						<div className={"c-users-disabled-filter"} style={{marginLeft: '12px'}}>
							<Switch
								label="Show disabled"
								checked={includeDisabled}
								onChange={this.handleIncludeDisabledChange}
							/>
						</div>
					</div>
					{this.renderPagination()}
				</div>
				<div style={{opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto'}}>
					<OrganisationUsersTable
						users={users}
						partnerBrands={this.props.partnerBrands}
						sort={this.state.sort}
						onSortChange={this.handleSortChange}
						loading={loading && !data}
						onRowClick={(user) => this.props.history.push(Routes.OrganisationUserEdit(user.id))}
						onClearFilters={this.handleClearFilters}
					/>
				</div>
				{/* Under the cards as well, shown on a phone only, as on the workspace lists. */}
				<div className={"c-workspace_bottom-pages"}>{this.renderPagination()}</div>
			</div>
		);
	}
}

function mapStateToProps(state: RootState) {
	return {
		partnerBrands: state.partnerBrand.partnerBrandsSearchMap
	};
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchPartnerBrands: (ids: string[], onFailure: OnFailure) => dispatch(fetchPartnerBrands(ids, () => {}, onFailure))
	};
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(OrganisationUserList));